import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  addLine,
  itemCount,
  lineFrom,
  reconcileLines,
  removeLine,
  sanitizeLines,
  savings,
  setQuantity,
  subtotal,
} from 'cart/cartLogic';

const CartContext = createContext(null);
export const CART_STORAGE_KEY = 'mxc.cart.v1';
export const COUPON_STORAGE_KEY = 'mxc.coupon.v1';

function readStored() {
  try {
    return sanitizeLines(JSON.parse(window.localStorage.getItem(CART_STORAGE_KEY) || '[]'));
  } catch {
    return [];
  }
}

function readCoupon() {
  try {
    const code = window.localStorage.getItem(COUPON_STORAGE_KEY);
    return code && /^[A-Za-z0-9][A-Za-z0-9_-]{2,39}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

/**
 * Cart state, persisted in localStorage and kept in sync across tabs.
 * Also owns the cart drawer's open state so "add to cart" anywhere can open it.
 */
export function CartProvider({ children }) {
  const [lines, setLines] = useState(readStored);
  // The coupon the shopper applied (already accepted once by the server); the
  // server re-checks it on every validation and at checkout.
  const [couponCode, setCouponCode] = useState(readCoupon);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  // Mirror of `lines` so add() can report what happened synchronously.
  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* storage full or blocked: the cart still works for this visit */
    }
  }, [lines]);

  useEffect(() => {
    try {
      if (couponCode) window.localStorage.setItem(COUPON_STORAGE_KEY, couponCode);
      else window.localStorage.removeItem(COUPON_STORAGE_KEY);
    } catch {
      /* storage blocked: the coupon lasts for this visit */
    }
  }, [couponCode]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CART_STORAGE_KEY) setLines(readStored());
      if (e.key === COUPON_STORAGE_KEY) setCouponCode(readCoupon());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /** Add a product variant. Returns { added, limited } and opens the drawer. */
  const add = useCallback((product, variant, quantity = 1, { openDrawer = true } = {}) => {
    const result = addLine(linesRef.current, lineFrom(product, variant), quantity);
    linesRef.current = result.lines;
    setLines(result.lines);
    setLastAdded({ variantId: variant.id, at: Date.now() });
    if (openDrawer) setDrawerOpen(true);
    return { added: result.added, limited: result.limited };
  }, []);

  const update = useCallback((variantId, quantity) => {
    setLines((current) => setQuantity(current, variantId, quantity));
  }, []);

  const remove = useCallback((variantId) => {
    setLines((current) => removeLine(current, variantId));
  }, []);

  /** The order was placed: the cart and its coupon are done with. */
  const clear = useCallback(() => {
    setLines([]);
    setCouponCode(null);
  }, []);

  const setCoupon = useCallback((code) => setCouponCode(code ? String(code).trim().toUpperCase() : null), []);
  const clearCoupon = useCallback(() => setCouponCode(null), []);

  /** Bring prices and stock limits up to date with a cart validation answer. */
  const reconcile = useCallback((serverLines, options) => {
    setLines((current) => reconcileLines(current, serverLines, options));
  }, []);

  const value = useMemo(
    () => ({
      lines,
      count: itemCount(lines),
      subtotal: subtotal(lines),
      savings: savings(lines),
      add,
      update,
      remove,
      clear,
      couponCode,
      setCoupon,
      clearCoupon,
      reconcile,
      lastAdded,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
    }),
    [lines, add, update, remove, clear, couponCode, setCoupon, clearCoupon, reconcile, lastAdded, drawerOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
